import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  BusinessDetailPayload,
  User,
  EmploymentDetailPayload,
  EmploymentDetail,
  ProfileImage,
  NewProfileImageResponse,
  BusinessDetails,
  OtpValidatePayload,
  changePasswordPayload,
} from "../api/models";
import {
  getReferralCode,
  getEmploymentDetail,
  updateEmploymentDetail,
  getUser,
  updateUser,
  getProfileImage,
  getBusinessDetails,
  updateBusinessDetails,
  uploadProfileImage,
  // otpSend,
  otpValidate,
} from "../api/users";
import { AxiosError } from "axios";

export const getUserReferralCode = createAsyncThunk(
  "user/getReferralCode",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getReferralCode();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const uploadUserProfileImage = createAsyncThunk(
  "user/uploadProfileImage",
  async (payload: ProfileImage, { rejectWithValue }) => {
    try {
      const res = await uploadProfileImage(payload);
      return res?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const getUserProfileImage = createAsyncThunk(
  "user/getProfileImage",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getProfileImage();
      return res?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const getUserEmploymentDetail = createAsyncThunk(
  "user/getEmploymentDetail",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getEmploymentDetail();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const updateUserEmploymentDetail = createAsyncThunk(
  "user/updateEmploymentDetail",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await updateEmploymentDetail(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const getUserDetails = createAsyncThunk(
  "user/getUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUser();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  "user/updateUserDetails",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await updateUser(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const getUserBusinessDetails = createAsyncThunk(
  "user/getBusinessDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getBusinessDetails();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const updateUserBusinessDetails = createAsyncThunk(
  "user/updateBusinessDetails",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await updateBusinessDetails(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

// export const sendOtpAsync = createAsyncThunk(
//   "user/send-otp",
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await otpSend();
//       return response?.data?.Data;
//     } catch (error) {
//       if (error instanceof AxiosError)
//         if (error?.response?.data || error?.message) {
//           return rejectWithValue(
//             error?.response?.data?.Message || error?.message
//           );
//         }
//     }
//   }
// );

export const validateOtpAsync = createAsyncThunk(
  "user/validate-otp",
  async(payload: OtpValidatePayload, {rejectWithValue}) => {
    try {
      const resp = await otpValidate(payload);
      return resp.data
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
)

interface UserState {
  referralCode: string;
  employmentDetail: EmploymentDetail;
  employmentDetailResp: string;
  userDetails: User;
  businessDetails: BusinessDetails;
  userProfileImage: NewProfileImageResponse;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  referralCode: "",
  employmentDetail: {} as EmploymentDetail,
  employmentDetailResp: "",
  userDetails: {} as User,
  businessDetails: {} as BusinessDetails,
  userProfileImage: {} as NewProfileImageResponse,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserDetailAfterLogin(state, action) {
      state.userDetails = { ...state.userDetails, ...action?.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserReferralCode.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getUserReferralCode.fulfilled, (state, action) => {
        state.loading = false;
        state.referralCode = action.payload ?? "";
      })
      .addCase(getUserReferralCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getUserEmploymentDetail.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getUserEmploymentDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.employmentDetail = action.payload ?? ({} as EmploymentDetail);
      })
      .addCase(getUserEmploymentDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserEmploymentDetail.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateUserEmploymentDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.employmentDetailResp = action.payload ?? "";
      })
      .addCase(updateUserEmploymentDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getUserDetails.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = { ...state.userDetails, ...action?.payload };
      })
      .addCase(getUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserDetails.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = {
          ...action?.payload,
          ...state.userDetails,
        } as User;
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getUserBusinessDetails.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getUserBusinessDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.businessDetails =
          action?.payload ?? ({} as BusinessDetails);
      })
      .addCase(getUserBusinessDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserBusinessDetails.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateUserBusinessDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.businessDetails =
          action?.payload ?? ({} as BusinessDetails);
      })
      .addCase(updateUserBusinessDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadUserProfileImage.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(uploadUserProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfileImage = action.payload as NewProfileImageResponse;
      })
      .addCase(uploadUserProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getUserProfileImage.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getUserProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfileImage = action.payload as NewProfileImageResponse;
      })
      .addCase(getUserProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUserDetailAfterLogin } = userSlice.actions;

export default userSlice.reducer;
